import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { concatMap, filter, tap } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { DialogConfirmPromptComponent } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.component';

import { showLegacyGoals } from '@libs/src/goal/account-goal.helper';
import {
  Account,
  AccountGroupMembership,
  AccountSegmentSelection,
} from '@libs/src/models/account.model';
import { Group, GroupCustomSegment } from '@libs/src/models/group.model';
import { updateSegmentSelections } from '@libs/src/segment-selections/segment-selections.helper';
import { SetUser } from '@libs/src/user/user.actions';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { IAppState } from '@main-client/src/app/app.state';
import { FeedRemoveGroup } from '@main-client/src/app/feed/feed.actions';
import { LoadProfile } from '@main-client/src/app/profile/profile-store/profile.actions';
import { isAdminOfGroup } from '@main-client/src/app/user/user.helpers';

import { AccountHubsEditService } from '../../account/account-hubs-edit.service';
import { GroupMembershipService } from '../../core/group-membership.service';
import { UserMutationService } from '../../user/user-mutation.service';

import castArray from 'lodash-es/castArray';
import chunk from 'lodash-es/chunk';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import get from 'lodash-es/get';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import reduce from 'lodash-es/reduce';
import without from 'lodash-es/without';

@Component({
  selector: 'app-profile-hub-item',
  templateUrl: './profile-hub-item.component.html',
})
export class ProfileHubItemComponent implements OnChanges {
  @Input() groupMembership: AccountGroupMembership;
  @Input() isIntroOnly: boolean;
  @Input() isUser: boolean;
  @Input() profile: Account;
  @Input() user: Account;
  @Output() remove = new EventEmitter();
  interestsChunks: string[][];
  isEditMode = false;
  isUserAdmin = false;
  industriesSelected: { [key: string]: boolean };
  offeringsSelected: { [key: string]: boolean };
  seekingSelected: { [key: string]: boolean };
  segmentationSelected: { [key: string]: string | string[] } = {};
  tenantState = this.store.select('tenant');
  initialSegmentationSelected: { [key: string]: string | string[] } = {};
  isLoading = false;
  group: Group;
  showEditLegacyGoals = true;

  constructor(
    private accountBackend: AccountBackend,
    private accountHubsEditService: AccountHubsEditService,
    private dialog: MatDialog,
    private groupMembershipService: GroupMembershipService,
    private store: Store<IAppState>,
    private toastService: ToastService,
    private userMutationService: UserMutationService,
  ) {}

  ngOnChanges() {
    const groupIndustries = get(
      this.groupMembership,
      'document.industries',
      [],
    );
    this.interestsChunks = chunk(
      groupIndustries,
      Math.ceil(groupIndustries.length / 2),
    );
    this.group = this.groupMembership.document;
    this.isUserAdmin = this.getUserAdmin();
    this.isEditMode = this.isUser || this.isUserAdmin;
    this.generatedSelectedLists();
    this.showEditLegacyGoals = showLegacyGoals(this.groupMembership);
  }

  generatedSelectedLists() {
    const groupIndustries = get(
      this.groupMembership,
      'document.industries',
      [],
    ) as AccountGroupMembership['industries'];
    this.industriesSelected = groupIndustries.reduce(
      (accumulator: any, item: string) => {
        return {
          ...accumulator,
          [item]: this.isSelected(this.groupMembership.industries, item),
        };
      },
      {},
    );

    const groupOfferings = this.groupMembership?.document?.offerings ?? [];
    this.offeringsSelected = groupOfferings.reduce(
      (accumulator: any, item: string) => {
        return {
          ...accumulator,
          [item]: this.isSelected(this.groupMembership.offerings, item),
        };
      },
      {},
    );
    this.seekingSelected = groupOfferings.reduce(
      (accumulator: any, item: string) => {
        return {
          ...accumulator,
          [item]: this.isSelected(this.groupMembership.seeking, item),
        };
      },
      {},
    );
    const segmentSelections = get(
      this.groupMembership,
      'segment_selections',
      [],
    );
    const customSegments = get(this.group, 'custom_segments', []);
    this.segmentationSelected = reduce(
      customSegments,
      (accumulator, customSegment: GroupCustomSegment) => {
        return {
          ...accumulator,
          [customSegment._id]: this.getSegmentSelection(
            segmentSelections,
            customSegment,
          ),
        };
      },
      {},
    );
    this.initialSegmentationSelected = cloneDeep(this.segmentationSelected);
  }

  getSegmentSelection(
    segmentSelections: AccountSegmentSelection[],
    customSegment: GroupCustomSegment,
  ) {
    const segmentSelection = find(segmentSelections, {
      custom_segment: customSegment._id,
    });
    if ('multi' !== customSegment.selection_type) {
      return get(segmentSelection, 'node', '');
    }
    return get(segmentSelection, 'multiselected_nodes', []);
  }

  getUserAdmin() {
    if (isEmpty(this.user)) {
      return false;
    }
    return this.user.isAdmin || isAdminOfGroup(this.user, this.group);
  }

  generateUserGroupMutation(
    fieldName: keyof AccountGroupMembership,
    updateValue: AccountGroupMembership[keyof AccountGroupMembership],
  ) {
    const update = { [fieldName]: updateValue };
    return this.isUser
      ? this.userMutationService.updateGroupMembership(
          this.groupMembership.document,
          update,
        )
      : this.accountHubsEditService.updateMembership(
          this.profile,
          this.groupMembership,
          update,
        );
  }

  onControlUpdate(
    fieldName: keyof AccountGroupMembership,
    updateValue: AccountGroupMembership[keyof AccountGroupMembership],
  ) {
    return this.generateUserGroupMutation(fieldName, updateValue)
      .pipe(
        tap((response: AccountGroupMembership[]) =>
          this.updateSuccess(fieldName, response),
        ),
      )
      .subscribe();
  }

  updateSuccess(
    fieldName: keyof AccountGroupMembership,
    response: AccountGroupMembership[],
  ) {
    this.toastService.showSuccess('Changes saved!');
    this.updateProfileStore(this.profile, response);
    const groupId = get(this.groupMembership, 'document._id');
    const updatedGroupMembership = getMembership(response, groupId);
    const update = pick(updatedGroupMembership, [fieldName]);
    this.groupMembership = { ...this.groupMembership, ...update };
    if (this.isUser) {
      const updatedUser = {
        ...this.profile,
        groups: response,
      };
      return this.store.dispatch(SetUser(updatedUser));
    }
  }

  onControlListUpdate(
    fieldName: keyof AccountGroupMembership,
    listItem: string,
    updateValue: boolean,
  ) {
    const updatedList = updateValue
      ? [...(this.groupMembership as any)[fieldName], listItem]
      : without((this.groupMembership as any)[fieldName], listItem);
    this.groupMembership = {
      ...this.groupMembership,
      [fieldName]: compact(updatedList),
    };
    this.generatedSelectedLists();
    return this.onControlUpdate(fieldName, updatedList);
  }

  promptDeleteGroup(membership: AccountGroupMembership) {
    const dialogConfig: any = {
      data: {
        title: $localize`:@@confirmPromptDialog.title.removeHub:Remove ${membership.document.name} Hub`,
        cancel: 'Cancel',
        confirm: 'Remove',
        content: 'Are you sure you want to remove this Hub?',
      },
    };
    this.dialog
      .open(DialogConfirmPromptComponent, dialogConfig)
      .afterClosed()
      .pipe(
        filter((dialogResponse: any) => dialogResponse),
        concatMap(() => {
          this.isLoading = true;
          return this.isUser
            ? this.userMutationService.deleteGroupMembership(
                membership.document,
              )
            : this.accountHubsEditService.deleteMembership(
                this.profile,
                membership,
              );
        }),
        tap((response: AccountGroupMembership[]) => {
          this.isLoading = false;
          this.toastService.showSuccess('Hub removed!');
          this.updateProfileStore(this.profile, response);
          this.remove.emit({ isRemoved: true });

          if (this.isUser) {
            this.store.dispatch(
              SetUser({
                groups: response,
              }),
            );
            this.store.dispatch(
              FeedRemoveGroup({
                group: membership.document,
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  isSelected(list: string[], value: string) {
    return includes(list, value);
  }

  updateProfileStore(
    currentProfile: Account,
    groupUpdate: AccountGroupMembership[],
  ) {
    return this.store.dispatch(
      LoadProfile({
        ...currentProfile,
        groups: groupUpdate,
      }),
    );
  }

  updateSegmentSelections(
    customSegment: GroupCustomSegment,
    selectedNodes: string | string[],
  ) {
    const nodeNamesUpdate: string[] = castArray(selectedNodes);
    const selections = updateSegmentSelections(
      this.groupMembership.segment_selections,
      customSegment,
      nodeNamesUpdate,
    );
    this.onControlUpdate('segment_selections', selections);
  }

  updateMultiSegmentSelections(
    customSegment: GroupCustomSegment,
    selectedNodes: string | string[],
    initialSelectedNodes: string | string[],
  ) {
    if (isEqual(selectedNodes, initialSelectedNodes)) {
      return;
    }
    this.updateSegmentSelections(customSegment, selectedNodes);
  }
}
